import { useCallback, useState } from 'react';

/** useState that remembers its value in localStorage (UI preferences only). */
export function useStoredState<T>(key: string, initial: T): [T, (next: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw === null ? initial : (JSON.parse(raw) as T);
    } catch {
      return initial;
    }
  });
  const set = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValue((prev) => {
        const v = typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
        try {
          localStorage.setItem(key, JSON.stringify(v));
        } catch {
          // preference just won't persist
        }
        return v;
      });
    },
    [key],
  );
  return [value, set];
}

/** A set of collapsed ids, remembered per key. */
export function useCollapsed(key: string): [Set<string>, (id: string) => void, (ids: string[]) => void] {
  const [list, setList] = useStoredState<string[]>(key, []);
  const toggle = useCallback((id: string) => setList((l) => (l.includes(id) ? l.filter((x) => x !== id) : [...l, id])), [setList]);
  return [new Set(list), toggle, setList];
}
